// The target size compressor on /tools/compress.
//
// The converter next door asks "what quality?" and tells you the size that came
// out. This one is the other way round: you name the size, it finds the quality.
// Same decode, canvas and encode underneath, so both pages agree on what a
// picture is; only the search in tools/target-size.js is new.
//
// Every setting re-runs the list, the way the converter does, so what is on
// screen always matches the controls above it.

import { compressToTarget } from './tools/target-size.js';
import {
    canEncode,
    decode,
    hasAlpha,
    sizeOf,
    renameExtension,
    saveBlob,
    formatBytes,
} from './tools/images.js';
import { wirePicker, blockStrayDrops, initColourPicker } from './tools/picker.js';
import { zip } from './tools/zip.js';
import { wireAccountSave } from './tools/account.js';

// Long enough that typing "500" does not start a run at "5" and again at "50".
const SETTLE_MS = 400;

const FORMATS = {
    'image/jpeg': { label: 'JPEG', extension: 'jpg', transparent: false },
    'image/webp': { label: 'WebP', extension: 'webp', transparent: true },
};

const el = id => document.getElementById(id);

const dom = {
    zone: el('compressZone'),
    input: el('compressInput'),
    panel: el('compressPanel'),
    target: el('compressTarget'),
    unit: el('compressUnit'),
    presets: document.querySelectorAll('[data-preset-kb]'),
    format: el('compressFormat'),
    formatNote: el('compressFormatNote'),
    backgroundRow: el('compressBackgroundRow'),
    background: el('compressBackground'),
    maxEdge: el('compressMaxEdge'),
    list: el('compressList'),
    summary: el('compressSummary'),
    downloadAll: el('compressDownloadAll'),
    clear: el('compressClear'),
    saveAccount: el('toolSaveAccount'),
    savePanel: el('toolSavePanel'),
    saveList: el('toolSaveList'),
    saveError: el('toolSaveError'),
};

const items = [];
let runToken = 0;
let settleTimer = null;

// Which lossy formats this browser will actually produce. Filled in at startup;
// until then the picker offers what the markup lists.
const encodable = new Set(Object.keys(FORMATS));

function targetBytes() {
    const amount = Number(dom.target.value) || 0;
    const unit = dom.unit.value === 'MB' ? 1024 * 1024 : 1024;
    return Math.max(1024, Math.round(amount * unit));
}

function settings() {
    return {
        target: targetBytes(),
        format: dom.format.value,
        background: dom.background.value,
        maxEdge: Number(dom.maxEdge.value) || 0,
    };
}

/**
 * The output type for one image.
 *
 * On automatic, transparency decides: JPEG has no alpha channel, so a cut-out
 * saved as one comes back with a filled rectangle behind it. WebP keeps it, and
 * is the better choice for a photograph too, so it only loses out when this
 * browser cannot write it.
 */
function formatFor(item, config) {
    if (config.format !== 'auto') return config.format;
    if (item.transparent && encodable.has('image/webp')) return 'image/webp';
    return encodable.has('image/jpeg') ? 'image/jpeg' : 'image/webp';
}

function syncControls() {
    const forced = dom.format.value;
    dom.formatNote.textContent = forced === 'auto'
        ? 'WebP for anything transparent, JPEG for the rest.'
        : `Every image is written as ${FORMATS[forced].label}.`;

    // The colour only matters where transparency has to go somewhere. On
    // automatic that never happens, because transparency picks WebP.
    dom.backgroundRow.hidden = forced !== 'image/jpeg';
}

function row(item) {
    const node = document.createElement('li');
    node.className = 'flex items-center gap-4 p-4';
    node.innerHTML = `
        <div class="h-14 w-14 shrink-0 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-white/10 bg-slate-100 dark:bg-gray-900/50">
            <img data-thumb alt="" class="h-full w-full object-cover" />
        </div>
        <div class="min-w-0 flex-1">
            <p data-name class="truncate text-sm font-semibold text-slate-900 dark:text-white"></p>
            <p data-detail class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Waiting</p>
            <p data-how class="mt-0.5 text-xs text-slate-500 dark:text-slate-400"></p>
        </div>
        <div class="flex items-center gap-2 shrink-0">
            <span data-badge class="hidden rounded-full px-2.5 py-1 text-xs font-semibold"></span>
            <button type="button" data-save disabled
                class="inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-medium bg-slate-100 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                <i class="fa-regular fa-arrow-down-to-line"></i>
                <span class="hidden sm:inline">Save</span>
            </button>
            <button type="button" data-remove aria-label="Remove"
                class="inline-flex items-center justify-center h-9 w-9 rounded-xl text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors">
                <i class="fa-regular fa-xmark"></i>
            </button>
        </div>`;

    node.querySelector('[data-name]').textContent = item.file.name;
    node.querySelector('[data-save]').addEventListener('click', () => {
        if (item.output) saveBlob(item.output.blob, item.output.name);
    });
    node.querySelector('[data-remove]').addEventListener('click', () => remove(item));

    item.node = node;
    return node;
}

/** The one line that says what the search actually did. */
function describe(output) {
    if (output.passthrough) {
        return 'Already under the target, so the original file is kept unchanged.';
    }

    const parts = [`quality ${Math.round(output.quality * 100)}%`];
    if (output.scale < 0.999) {
        parts.push(`scaled to ${Math.round(output.scale * 100)}% (${output.width} x ${output.height})`);
    }
    return parts.join(', ');
}

function paint(item) {
    if (!item.node) return;

    const thumb = item.node.querySelector('[data-thumb]');
    const detail = item.node.querySelector('[data-detail]');
    const how = item.node.querySelector('[data-how]');
    const badge = item.node.querySelector('[data-badge]');
    const save = item.node.querySelector('[data-save]');

    if (item.error) {
        detail.textContent = item.error;
        detail.className = 'mt-0.5 text-xs text-red-600 dark:text-red-400';
        how.textContent = '';
        badge.classList.add('hidden');
        save.disabled = true;
        return;
    }

    detail.className = 'mt-0.5 text-xs text-slate-500 dark:text-slate-400';

    if (!item.output) {
        detail.textContent = 'Searching for the right quality';
        how.textContent = '';
        badge.classList.add('hidden');
        save.disabled = true;
        return;
    }

    const { blob, name, reached } = item.output;
    thumb.src = item.output.preview;
    detail.textContent = `${formatBytes(item.file.size)} to ${formatBytes(blob.size)} - ${name}`;
    how.textContent = describe(item.output);
    save.disabled = false;

    badge.classList.remove('hidden');
    if (!reached) {
        badge.textContent = 'over target';
        badge.className = 'rounded-full px-2.5 py-1 text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
        return;
    }

    const saved = Math.round((1 - blob.size / item.file.size) * 100);
    badge.textContent = saved > 0 ? `-${saved}%` : 'unchanged';
    badge.className = saved > 0
        ? 'rounded-full px-2.5 py-1 text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
        : 'rounded-full px-2.5 py-1 text-xs font-semibold bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-slate-300';
}

function paintSummary() {
    const done = items.filter(item => item.output);
    dom.downloadAll.disabled = done.length === 0;

    if (!items.length) {
        dom.summary.textContent = '';
        return;
    }

    const settled = items.filter(item => item.output || item.error).length;
    if (settled < items.length) {
        dom.summary.textContent = `Working on ${settled + 1} of ${items.length}`;
        return;
    }

    if (!done.length) {
        dom.summary.textContent = 'Nothing could be compressed';
        return;
    }

    const before = done.reduce((sum, item) => sum + item.file.size, 0);
    const after = done.reduce((sum, item) => sum + item.output.blob.size, 0);
    const saved = before ? Math.round((1 - after / before) * 100) : 0;
    const missed = done.filter(item => !item.output.reached).length;
    // A size reached by shrinking the picture is a compromise, and the
    // percentage alone would celebrate it. Said here, not only per row.
    const shrunk = done.filter(item => !item.output.passthrough && item.output.scale < 0.999).length;

    const notes = [`${done.length} ${done.length === 1 ? 'image' : 'images'}, ${formatBytes(before)} to ${formatBytes(after)}, ${saved}% smaller`];
    if (shrunk) notes.push(`${shrunk === done.length && shrunk > 1 ? 'All' : shrunk} had to be scaled down to get there`);
    if (missed) notes.push(`${missed} could not reach the target`);
    dom.summary.textContent = notes.join('. ') + (notes.length > 1 ? '.' : '');
}

function releaseOutput(item) {
    if (item.output) URL.revokeObjectURL(item.output.preview);
    item.output = null;
}

async function compressOne(item, config) {
    const mime = formatFor(item, config);
    const result = await compressToTarget(item.image, {
        targetBytes: config.target,
        mime,
        // Only ever handed to the encoder that needs it. Filling a colour behind
        // a WebP would destroy the transparency it was chosen to keep.
        background: FORMATS[mime].transparent ? null : config.background,
        maxEdge: config.maxEdge,
    });

    const source = sizeOf(item.image);

    // Re-encoding a file that was already small enough can easily make it
    // bigger, and handing someone a worse copy of a file that never needed
    // touching is the one outcome this tool must not produce.
    if (item.file.size <= config.target && result.blob.size >= item.file.size) {
        return {
            blob: item.file,
            name: item.file.name,
            width: source.width,
            height: source.height,
            quality: 1,
            scale: 1,
            reached: true,
            passthrough: true,
            preview: URL.createObjectURL(item.file),
        };
    }

    return {
        blob: result.blob,
        name: renameExtension(item.file.name, FORMATS[mime].extension),
        width: result.width,
        height: result.height,
        quality: result.quality,
        scale: result.scale,
        reached: result.reached,
        passthrough: false,
        preview: URL.createObjectURL(result.blob),
    };
}

/**
 * Compress everything with the current settings.
 *
 * One at a time, and each one is nine or so encodes rather than the converter's
 * single pass, so this is the page where doing them in parallel would actually
 * lock the tab up.
 */
async function run() {
    const token = (runToken += 1);
    const config = settings();

    for (const item of items) {
        releaseOutput(item);
        if (item.image) item.error = null;
        paint(item);
    }
    paintSummary();

    for (const item of [...items]) {
        if (token !== runToken) return;
        if (!items.includes(item) || !item.image) continue;
        try {
            const output = await compressOne(item, config);
            if (token !== runToken) {
                URL.revokeObjectURL(output.preview);
                return;
            }
            item.output = output;
            item.error = null;
        } catch (err) {
            item.error = err.message || 'Could not compress this image';
        }
        paint(item);
        paintSummary();
    }
}

function schedule() {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(run, SETTLE_MS);
}

function remove(item) {
    const index = items.indexOf(item);
    if (index === -1) return;
    releaseOutput(item);
    item.node?.remove();
    if (item.image?.close) item.image.close();
    items.splice(index, 1);
    paintSummary();
    togglePanel();
}

function togglePanel() {
    dom.panel.hidden = items.length === 0;
}

async function add(files) {
    for (const file of files) {
        const item = { file, image: null, transparent: false, output: null, error: null, node: null };
        try {
            item.image = await decode(file);
            item.transparent = hasAlpha(item.image);
        } catch (err) {
            item.error = err.message || 'Could not read this image';
        }
        items.push(item);
        dom.list.appendChild(row(item));
        paint(item);
    }

    togglePanel();
    run();
}

async function downloadAll() {
    const done = items.filter(item => item.output);
    if (!done.length) return;

    if (done.length === 1) {
        saveBlob(done[0].output.blob, done[0].output.name);
        return;
    }

    dom.downloadAll.disabled = true;
    try {
        const used = new Map();
        const entries = done.map(item => {
            const name = item.output.name;
            const seen = used.get(name) || 0;
            used.set(name, seen + 1);
            return {
                name: seen ? name.replace(/(\.[^.]+)$/, ` (${seen})$1`) : name,
                blob: item.output.blob,
            };
        });
        saveBlob(await zip(entries), 'compressed-images.zip');
    } finally {
        dom.downloadAll.disabled = false;
    }
}

/** Drop the formats this browser cannot write, from the set and from the picker. */
async function checkEncoders() {
    for (const mime of Object.keys(FORMATS)) {
        if (await canEncode(mime)) continue;
        encodable.delete(mime);
        dom.format.querySelector(`option[value="${mime}"]`)?.remove();
    }
    syncControls();
}

function markPreset(kb) {
    for (const button of dom.presets) {
        const active = Number(button.dataset.presetKb) === kb;
        button.setAttribute('aria-pressed', String(active));
    }
}

function applyPreset(kb) {
    if (kb >= 1024 && kb % 1024 === 0) {
        dom.target.value = String(kb / 1024);
        dom.unit.value = 'MB';
    } else {
        dom.target.value = String(kb);
        dom.unit.value = 'KB';
    }
    markPreset(kb);
    run();
}

function syncPresetFromInput() {
    markPreset(Math.round(targetBytes() / 1024));
}

function init() {
    if (!dom.zone) return;

    blockStrayDrops();
    initColourPicker();
    wirePicker({ zone: dom.zone, input: dom.input, multiple: true, onFiles: add });

    checkEncoders();
    syncPresetFromInput();

    for (const button of dom.presets) {
        button.addEventListener('click', () => applyPreset(Number(button.dataset.presetKb)));
    }

    dom.target.addEventListener('input', () => { syncPresetFromInput(); schedule(); });
    dom.unit.addEventListener('change', () => { syncPresetFromInput(); run(); });
    dom.format.addEventListener('change', () => { syncControls(); run(); });
    dom.background.addEventListener('input', schedule);
    dom.maxEdge.addEventListener('input', schedule);

    dom.downloadAll.addEventListener('click', downloadAll);

    wireAccountSave({
        button: dom.saveAccount,
        panel: dom.savePanel,
        list: dom.saveList,
        error: dom.saveError,
        collect: () => items
            .filter(item => item.output)
            .map(item => ({ blob: item.output.blob, name: item.output.name })),
    });
    dom.clear.addEventListener('click', () => {
        while (items.length) remove(items[0]);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
